/**
 * EventNoteDialog — Add or edit a note on an event.
 *
 * Supports two modes:
 *   - Add mode (default): fresh form, title "Add Note"
 *   - Edit mode: when `existingNote` is provided, pre-fills form,
 *     title "Edit Note", save includes the note_id
 *
 * Props:
 *   event              — { event_id, notes: [...] }
 *   prenoteData        — array of { preform_note }
 *   eventNotesPreform  — boolean (campus config)
 *   eventNotesFreeform — boolean (campus config)
 *   tzOffset           — timezone offset for display
 *   onClose            — () => void
 *   onSave             — (eventId, noteText, noteId?) => void
 *   saving             — boolean
 *   existingNote       — (optional) { note_id, note } — when provided, enables edit mode
 */

import { useState, useCallback } from 'react';
import { convertIsoUTCToLocal } from '../../../../libs.jsx';

const EventNoteDialog = ({
    event, prenoteData, eventNotesPreform, eventNotesFreeform,
    tzOffset, onClose, onSave, saving, existingNote,
}) => {
    const isEditMode = Boolean(existingNote);
    const hasPreform = Boolean(eventNotesPreform) && Array.isArray(prenoteData) && prenoteData.length > 0;
    const hasFreeform = Boolean(eventNotesFreeform) || !hasPreform;

    // In edit mode, default to freeform with existing text pre-filled
    const [noteType, setNoteType] = useState(isEditMode ? 'freeform' : (hasPreform ? 'preform' : 'freeform'));
    const [selectedNote, setSelectedNote] = useState('');
    const [freeformNote, setFreeformNote] = useState(isEditMode ? (existingNote?.note || '') : '');

    const showTypeSelector = hasPreform && hasFreeform;

    const handleTypeChange = useCallback((type) => {
        setNoteType(type);
        setSelectedNote('');
        setFreeformNote('');
    }, []);

    const handleSave = useCallback(() => {
        const noteText = noteType === 'preform' ? selectedNote : freeformNote;
        if (!noteText?.trim()) return;
        if (isEditMode) {
            onSave(event.event_id, noteText, existingNote.note_id);
        } else {
            onSave(event.event_id, noteText);
        }
    }, [noteType, selectedNote, freeformNote, event, onSave, isEditMode, existingNote]);

    const isValid = noteType === 'preform'
        ? selectedNote.length > 0
        : freeformNote.trim().length > 0;

    const existingNotes = event?.notes || [];

    return (
        <div className="pp-dialog-backdrop" onClick={onClose}>
            <div className="pp-dialog pp-dialog--wide" onClick={(e) => e.stopPropagation()}>
                <div className="pp-dialog__header">
                    <h2 className="pp-dialog__title">{isEditMode ? 'Edit Note' : 'Add Note'}</h2>
                    <button className="dp-drawer__close" onClick={onClose}>{'\u2715'}</button>
                </div>
                <div className="pp-dialog__body">

                    {/* Note type selector — only when both modes available */}
                    {showTypeSelector ? (
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">Note Type</label>
                            <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
                                <label className="dp-drawer__label--inline" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="noteType"
                                        checked={noteType === 'preform'}
                                        onChange={() => handleTypeChange('preform')}
                                    />
                                    Preformatted Note
                                </label>
                                <label className="dp-drawer__label--inline" style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-1)', cursor: 'pointer' }}>
                                    <input
                                        type="radio"
                                        name="noteType"
                                        checked={noteType === 'freeform'}
                                        onChange={() => handleTypeChange('freeform')}
                                    />
                                    Custom Note
                                </label>
                            </div>
                        </div>
                    ) : null}

                    {/* Preformatted note dropdown */}
                    {noteType === 'preform' && hasPreform ? (
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">Select Note</label>
                            <select
                                className="dp-drawer__select"
                                value={selectedNote}
                                onChange={(e) => setSelectedNote(e.target.value)}
                            >
                                <option value="">Select a note…</option>
                                {prenoteData.map((pn, i) => (
                                    <option key={i} value={pn.preform_note}>{pn.preform_note}</option>
                                ))}
                            </select>
                        </div>
                    ) : null}

                    {/* Freeform textarea */}
                    {noteType === 'freeform' ? (
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">Note</label>
                            <textarea
                                className="dp-drawer__textarea"
                                value={freeformNote}
                                onChange={(e) => setFreeformNote(e.target.value)}
                                placeholder="Enter your note…"
                                rows={4}
                            />
                        </div>
                    ) : null}

                    {/* Existing notes */}
                    {existingNotes.length > 0 ? (
                        <div style={{ marginTop: 'var(--space-4)' }}>
                            <h4 style={{ fontSize: 'var(--font-size-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text)', marginBottom: 'var(--space-2)' }}>
                                Existing Notes ({existingNotes.length})
                            </h4>
                            <div className="dp-table-wrap">
                                <table className="dp-table">
                                    <thead className="dp-thead">
                                        <tr>
                                            <th className="dp-th">Timestamp</th>
                                            <th className="dp-th">Created By</th>
                                            <th className="dp-th">Note</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {existingNotes.map((note, idx) => (
                                            <tr key={idx} className="dp-tr">
                                                <td className="dp-td">
                                                    <span className="dp-cell-text dp-cell-text--muted" style={{ fontSize: 'var(--font-size-xs)', fontFamily: 'monospace' }}>
                                                        {note.create_time ? convertIsoUTCToLocal(note.create_time, tzOffset) : '\u2014'}
                                                    </span>
                                                </td>
                                                <td className="dp-td">
                                                    <span className="dp-cell-text">{note.display_name || '\u2014'}</span>
                                                </td>
                                                <td className="dp-td">
                                                    <span className="dp-cell-text">{note.note || '\u2014'}</span>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className="pp-dialog__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose} disabled={saving}>
                        Cancel
                    </button>
                    <button
                        className="dp-btn dp-btn--primary"
                        onClick={handleSave}
                        disabled={saving || !isValid}
                    >
                        {saving ? 'Saving\u2026' : (isEditMode ? 'Save Changes' : 'Save Note')}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EventNoteDialog;
